import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { FunctionalSelect, SelectItem } from '../components/ui/Select';
import { organizationsService } from '../services/organizationsService';
import {
  offerStudioService,
  allAudienceRules,
  attributesNeedingColumn,
  audienceRuleCount,
  decodeRules,
  encodeRules,
  flavorLabel,
  removeAudienceGroup,
  removeAudienceRule,
  findingDirection,
  ruleFromFinding,
  rulePresence,
  setActiveAudienceGroup,
  setAudienceGroupsOp,
  startAudienceGroup,
  toggleAudienceRule,
  writeLastOffer,
} from '../services/offerStudioService';
import { criteriaFromOfferSeed } from '../components/segmentBuilder/offerValueRules';
import { useCriteriaCount } from '../components/segmentBuilder/useCriteriaCount';
import { wakeSqlWarehouse } from '../services/sqlWarehouseService';
import KpiStrip from '../components/offerStudio/KpiStrip';
import BaselineSelect from '../components/offerStudio/BaselineSelect';
import CategoryNav from '../components/offerStudio/CategoryNav';
import FeatureCard from '../components/offerStudio/FeatureCard';
import InsightList from '../components/offerStudio/InsightList';
import RulesPanel from '../components/offerStudio/RulesPanel';
import { fmtDate } from '../components/offerStudio/format';
import { OFFER_REPORT_LIST_PAGE_KEY, listReturnPath } from '../utils/listPage';
import { ORG_LOCAL_SUFFIX, readOrgFlag, writeOrgLocal } from '../utils/orgLocalStorage';

const BASELINE_SHORT = { universe: 'universe', clickers: 'clickers', vertical: 'vertical' };
const NO_SUGGESTIONS_NOTE = /No include or exclude suggestions are available for this report\.\s*/gi;

function displayOfferNote(raw) {
  return String(raw || '')
    .replace(NO_SUGGESTIONS_NOTE, '')
    .replace(/this offers clicks/gi, "this offer's clicks")
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export default function OfferAudienceStudio() {
  const { offerId: offerIdParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const offerListPath = listReturnPath('/offer-report', location, OFFER_REPORT_LIST_PAGE_KEY);

  const [organizations, setOrganizations] = useState([]);
  const [organizationId, setOrganizationId] = useState('');

  const offerId = offerIdParam || '';
  const baseline = searchParams.get('baseline') || 'clickers';
  const category = searchParams.get('category') || '';
  const [audience, setAudience] = useState(() => decodeRules(searchParams.get('rules')));
  const [match, setMatch] = useState(searchParams.get('match') === 'ANY' ? 'ANY' : 'ALL');

  const [overview, setOverview] = useState(null);
  const [features, setFeatures] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ---- URL state helpers ----------------------------------------------------
  const setParam = useCallback(
    (key, val) => {
      const next = new URLSearchParams(searchParams);
      if (val) next.set(key, val);
      else next.delete(key);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    const enc = encodeRules(audience);
    if (enc) next.set('rules', enc);
    else next.delete('rules');
    if (match === 'ANY') next.set('match', 'ANY');
    else next.delete('match');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audience, match]);

  // ---- organizations --------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const orgs = await organizationsService.getOrganizations();
        if (cancelled) return;
        setOrganizations(orgs);
        const saved = organizationsService.getSelectedOrganization('id');
        const pick = saved && orgs.some((o) => String(o.id) === String(saved)) ? String(saved) : orgs[0] ? String(orgs[0].id) : '';
        setOrganizationId(pick);
      } catch {
        if (!cancelled) setError('Failed to load organizations.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- report data ----------------------------------------------------------
  useEffect(() => {
    if (!organizationId || !offerId) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    writeLastOffer(offerId, organizationId);
    (async () => {
      try {
        const ov = await offerStudioService.overview(offerId, organizationId, baseline);
        if (cancelled) return;
        setOverview(ov);
        const [feats, ins] = await Promise.all([
          offerStudioService.features(offerId, organizationId, baseline, category),
          offerStudioService.insights(offerId, organizationId, baseline, 12),
        ]);
        if (cancelled) return;
        setFeatures(feats);
        setInsights(ins);
      } catch (e) {
        if (cancelled) return;
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 400 && baseline !== 'clickers') {
          setParam('baseline', 'clickers');
          return;
        }
        setError(typeof detail === 'string' ? detail : 'Failed to load the offer report.');
        setOverview(null);
        setFeatures([]);
        setInsights([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, offerId, baseline, category]);

  // ---- rules ----------------------------------------------------------------
  const findingKey = (featureOrInsight, value) => {
    const f = value ? featureOrInsight : { feature: featureOrInsight.feature };
    const v = value || { value: featureOrInsight.value };
    return [f.feature, v.value];
  };

  const isRuleActive = useCallback((featureOrInsight, value) => {
    const [feature, val] = findingKey(featureOrInsight, value);
    const where = rulePresence(audience, feature, val);
    return where === 'active' || where === 'exclude';
  }, [audience]);

  const isRuleElsewhere = useCallback((featureOrInsight, value) => {
    const [feature, val] = findingKey(featureOrInsight, value);
    return rulePresence(audience, feature, val) === 'elsewhere';
  }, [audience]);

  const activeDirection = useCallback((featureOrInsight, value) => {
    const [feature, val] = findingKey(featureOrInsight, value);
    return findingDirection(audience, feature, val);
  }, [audience]);

  const toggleRule = useCallback((featureOrInsight, value, options) => {
    const feature = value
      ? featureOrInsight
      : { feature: featureOrInsight.feature, display_label: featureOrInsight.display_label, segment_column: featureOrInsight.segment_column };
    const v = value || {
      value: featureOrInsight.value,
      value_label: featureOrInsight.value_label,
      index: featureOrInsight.index,
      segment_condition: featureOrInsight.segment_condition,
      include_rule: featureOrInsight.include_rule,
      exclude_rule: featureOrInsight.exclude_rule,
      include_label: featureOrInsight.include_label,
      exclude_label: featureOrInsight.exclude_label,
    };
    setAudience((prev) => toggleAudienceRule(prev, ruleFromFinding(feature, v, options)));
  }, []);

  const removeRule = useCallback((rule) => setAudience((prev) => removeAudienceRule(prev, rule)), []);

  // ---- live size ------------------------------------------------------------
  // The same tree the segment editor would be handed, so the number here is the
  // number there.
  const ruleList = useMemo(() => allAudienceRules(audience), [audience]);
  const criteria = useMemo(
    () => (audienceRuleCount(audience) ? criteriaFromOfferSeed(audience, match) : null),
    [audience, match]
  );
  const needColumn = useMemo(() => attributesNeedingColumn(ruleList), [ruleList]);
  const [autoCount, setAutoCount] = useState(true);
  useEffect(() => {
    if (!organizationId) return;
    setAutoCount(readOrgFlag(organizationId, ORG_LOCAL_SUFFIX.offerStudioAutoCount));
  }, [organizationId]);
  const size = useCriteriaCount(criteria, organizationId, { auto: autoCount });

  const toggleAutoCount = useCallback((v) => {
    setAutoCount(v);
    writeOrgLocal(organizationId, ORG_LOCAL_SUFFIX.offerStudioAutoCount, String(v));
  }, [organizationId]);

  // Counting is what needs a warm warehouse, so browsing an offer does not pay for one.
  const hasRules = audienceRuleCount(audience) > 0;
  const wokeWarehouse = useRef(false);
  useEffect(() => {
    if (!hasRules || wokeWarehouse.current) return;
    wokeWarehouse.current = true;
    wakeSqlWarehouse();
  }, [hasRules]);

  const buildSegment = useCallback(() => {
    const enc = encodeRules(audience);
    const q = new URLSearchParams({ offer: String(offerId), rules: enc, match });
    navigate(`/segments/new?${q.toString()}`);
  }, [audience, offerId, match, navigate]);

  const presets = useMemo(() => insights.filter((i) => i.direction === 'over').slice(0, 4), [insights]);

  // The lite report has no converter distribution to compare (the warehouse
  // withholds converter shares below 1k converters), so it reads clickers
  // against the list. The backend coerces the baseline, so trust what it echoes
  // back rather than the URL the page asked with.
  const focusLabel = overview?.focus === 'clickers' ? 'clickers' : 'converters';
  const effectiveBaseline = overview?.baseline || baseline;
  const baselineLabel =
    overview?.focus === 'clickers'
      ? overview?.baseline_label || 'our list'
      : BASELINE_SHORT[effectiveBaseline] || effectiveBaseline;
  const offer = overview?.offer;
  const notesText = displayOfferNote(offer?.note);
  const computed = fmtDate(overview?.computed_at);
  const headerOfferId = offer?.offer_id || offerId;
  const offerDetail = [offer?.offer_name, offer?.category, computed ? `computed ${computed}` : null]
    .filter(Boolean)
    .join(' · ');

  const jumpToFeature = useCallback(
    (i) => {
      if (i.category && i.category !== category) setParam('category', i.category);
      window.setTimeout(() => {
        document.getElementById(`feature-${i.feature}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    },
    [category, setParam]
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => navigate(offerListPath)} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800">
            <ArrowLeft className="h-4 w-4" /> Offer list
          </button>
          <h1 className="text-xl font-semibold text-gray-900">
            {headerOfferId ? `${headerOfferId}: ` : null}
            Offer audience
          </h1>
          {offer ? (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
              {flavorLabel(offer)}
            </span>
          ) : null}
          {offerDetail ? <span className="text-sm text-gray-500 truncate">— {offerDetail}</span> : null}
        </div>
        <div className="flex flex-col lg:flex-row lg:items-end gap-3">
          {organizations.length > 1 ? (
            <FunctionalSelect
              label="Organization"
              value={organizationsService.getSelectedOptionValue(organizations, organizationId, 'id')}
              onValueChange={(value) => {
                const parsed = organizationsService.parseOrganizationOptionValue(value);
                const id = parsed?.id != null ? String(parsed.id) : String(value);
                setOrganizationId(id);
                organizationsService.setSelectedOrganization(id, 'id', organizations);
              }}
              className="w-full lg:w-56"
            >
              {organizations.map((o) => (
                <SelectItem key={o.id} value={organizationsService.getSelectedOptionValue(organizations, String(o.id), 'id')}>
                  {o.name}
                </SelectItem>
              ))}
            </FunctionalSelect>
          ) : null}
          <BaselineSelect value={baseline} onChange={(b) => setParam('baseline', b === 'clickers' ? '' : b)} available={overview?.available_baselines} offerCategory={offer?.category} />
        </div>
      </div>

      {error ? <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div> : null}
      {overview && overview.focus !== 'clickers' && overview.has_converter_data === false ? (
        <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          This offer has no converter distribution yet, so only the {baselineLabel} side can be shown. The metrics
          job left converter shares empty for it; they will be derived from converter counts once the updated
          load_offers job has run.
        </div>
      ) : null}

      <KpiStrip overview={overview} loading={loading} focus={overview?.focus} flavor={offer?.flavor} />

      {notesText ? (
        <section className="rounded-lg border bg-white p-4" aria-label="Notes">
          <h2 className="text-sm font-semibold text-gray-900 mb-1">Notes</h2>
          <p className="m-0 whitespace-pre-wrap text-sm text-gray-700">{notesText}</p>
        </section>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-[16rem_1fr] gap-4">
        {/* Left rail */}
        <aside className="space-y-3">
          <RulesPanel
            audience={audience}
            match={match}
            onMatchChange={(m) => {
              setMatch(m);
              setAudience((prev) => setAudienceGroupsOp(prev, m === 'ANY' ? 'OR' : 'AND'));
            }}
            onRemove={removeRule}
            onReset={() => setAudience(decodeRules(''))}
            onStartGroup={() => {
              setMatch('ANY');
              setAudience((prev) => startAudienceGroup(prev, 'OR'));
            }}
            onActivateGroup={(id) => setAudience((prev) => setActiveAudienceGroup(prev, id))}
            onRemoveGroup={(id) => {
              setAudience((prev) => {
                const next = removeAudienceGroup(prev, id);
                if (next.groups.length <= 1) setMatch('ALL');
                return next;
              });
            }}
            onBuildSegment={buildSegment}
            presets={presets}
            onAddPreset={(p) => toggleRule(p)}
            isRuleActive={(p) => isRuleActive(p)}
            criteria={criteria}
            size={size}
            attributesNeedingColumn={needColumn}
            autoCount={autoCount}
            onToggleAutoCount={toggleAutoCount}
          />
          <CategoryNav categories={overview?.categories || []} selected={category} onSelect={(c) => setParam('category', c)} />
          {overview?.data_notes?.length ? (
            <ul className="text-[11px] text-gray-500 space-y-1 px-1">
              {overview.data_notes.map((n) => (
                <li key={n}>• {n}</li>
              ))}
            </ul>
          ) : null}
        </aside>

        {/* Main */}
        <main className="space-y-4 min-w-0">
          {audience.groups.length > 1 ? (
            <div className="sticky top-0 z-10 flex items-center gap-2 rounded-lg border bg-white px-3 py-2 text-sm shadow-sm">
              <span className="text-gray-500">Adding to</span>
              <select
                value={audience.activeGroupId}
                onChange={(e) => setAudience((prev) => setActiveAudienceGroup(prev, e.target.value))}
                className="rounded border px-2 py-1 text-sm"
                aria-label="Active audience group"
              >
                {audience.groups.map((g, i) => (
                  <option key={g.id} value={g.id}>
                    {g.name || `Group ${i + 1}`}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          {!category ? (
            <section className="rounded-lg border bg-white p-4" aria-label="Insights">
              <h2 className="text-sm font-semibold text-gray-900 mb-1">
                What sets {focusLabel} apart <span className="text-gray-400 font-normal">vs {baselineLabel}</span>
              </h2>
              <InsightList insights={insights} baselineLabel={baselineLabel} focusLabel={focusLabel} hasConverterData={overview?.has_converter_data !== false} loading={loading && !insights.length} onAddRule={(i, opts) => toggleRule(i, undefined, opts)} isRuleActive={(i) => isRuleActive(i)} isRuleElsewhere={(i) => isRuleElsewhere(i)} activeDirection={(i) => activeDirection(i)} onJumpToFeature={jumpToFeature} />
            </section>
          ) : null}

          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">
              {category ? overview?.categories?.find((c) => c.id === category)?.label || 'Category' : 'All features'}
              <span className="ml-2 text-gray-400 font-normal">{features.length} feature{features.length === 1 ? '' : 's'} · sorted by lift</span>
            </h2>
          </div>

          {loading && !features.length ? <div className="text-sm text-gray-500">Loading…</div> : null}
          {!loading && !features.length && offerId ? <div className="text-sm text-gray-500">No feature data for this offer.</div> : null}

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {features.map((f) => (
              <div key={f.feature} id={`feature-${f.feature}`} className="h-full">
                <FeatureCard feature={f} baselineLabel={baselineLabel} focusLabel={focusLabel} onAddRule={toggleRule} isRuleActive={isRuleActive} isRuleElsewhere={isRuleElsewhere} />
              </div>
            ))}
          </div>
        </main>
      </div>
    </div>
  );
}
